import Layers from "slotworks/graphics/layers/layers";
import Rectangle from "slotworks/utils/geom/rectangle";
import * as TweenJS from "slotworks/utils/tween";
import Contract from "slotworks/utils/contract";
import AnimatedSprite from "slotworks/graphics/animation/animated-sprite";
import graphicsManager from "slotworks/graphics/graphics-manager";
import { sequence, parallel } from "slotworks/utils/race-utils";
import soundManager from "slotworks/sound/sound-manager";
import DSComponents from "components/components";
import Logger = require("js-logger");

export default class Pot {

    public readonly ID:number;

    public static readonly LETTERS: string[] = ["A", "B", "C"];

    private coins: number;

    private coinsBanked: number;

    private triggered: boolean;

    private potsLayer: Layers;
    private animationsLayer: Layers;

    private coinsTops: string[][];
    private coinHighlights: string[];
    private coinsBankeds: string[];

    private coinsTopSprites: PIXI.Sprite[][];
    private coinHighlightSprites: PIXI.Sprite[];
    private coinsBankedSprites: PIXI.Sprite[];

    private idleAnimation: AnimatedSprite = null;
    private idle2Animation: AnimatedSprite = null;
    private potSprite:PIXI.Sprite;

    // fix for the moment pot burst before highlight tweens finish
    private idleAnimationToBeShown: boolean;
    private idle2AnimationToBeShown: boolean;

    private shakeAngles: number[];

    constructor(id: number) {
        this.ID = id;

        this.coins = 0;

        this.coinsTops = [[`collect_pot${this.ID}_coin_A`],
                                [`collect_pot${this.ID}_coin_B`, `collect_pot${this.ID}_coin_shadow_B`],
                                [`collect_pot${this.ID}_coin_C`, `collect_pot${this.ID}_coin_shadow_C`]];

        this.coinHighlights = [ `collect_pot${this.ID}_coin_A_highlight`, `collect_pot${this.ID}_coin_B_highlight`, `collect_pot${this.ID}_coin_C_highlight` ];

        this.coinsBankeds = [ `collect_pot${this.ID}_banked_coin_A`, `collect_pot${this.ID}_banked_coin_B`, `collect_pot${this.ID}_banked_coin_C` ];        

        this.coinsTopSprites = [];
        this.coinHighlightSprites = [];
        this.coinsBankedSprites = [];

        this.potsLayer = Layers.Custom.get("CoinCollectPots");
        this.animationsLayer = Layers.Custom.get("CoinCollectPotsAnimations");

        this.shakeAngles = [1, -1, 1, -2, 2, -3, 0];     
        this.shakeAngles.forEach((e, i, arr) => arr[i] = 3 * e * Math.PI / 180); // * 2 for more shaking
    }

    public init() {
        this.coins = 0;
        this.coinsBanked = 0;
        this.triggered = false;

        this.potSprite = this.potsLayer.getSprite(`collect_pot${this.ID}`);
        this.potSprite.anchor.set(0.5, 0.5);
        this.potSprite.x += this.potSprite.width/2;
        this.potSprite.y += this.potSprite.height/2;

        for(let s = 0; s < this.coinsTops.length; s++) {
            this.coinsTopSprites[s] = [];                   
            for(let i = this.coinsTops[s].length - 1; i >= 0; i--) {                  
                const sprite = this.potsLayer.getSprite(this.coinsTops[s][i]);
                sprite.visible = false;
                this.coinsTopSprites[s].push(sprite);
                sprite.x -= this.potSprite.x;
                sprite.y -= this.potSprite.y;
                this.potSprite.addChild(sprite);
            }
        }
        for(let sprite of this.coinHighlights) {
            this.potsLayer.getSprite(sprite).visible = false;
        }
        for(let s = 0; s < this.coinsBankeds.length; s++) {
            const sprite = this.potsLayer.getSprite(this.coinsBankeds[s]);
            sprite.anchor.set(0.5, 0.5);
            sprite.visible = false;
            sprite.x = sprite.x - this.potSprite.x + sprite.width/2;
            sprite.y = sprite.y - this.potSprite.y + sprite.height/2;
            this.potSprite.addChild(sprite);
            this.coinsBankedSprites[s] = sprite;
        }
    }

    public setTriggered(triggered:boolean) {
        this.triggered = triggered;
    }

    public getTriggered() {
        return this.triggered;
    }

    // on recovery, do I need a check
    public setCoins(coins: number) {
        this.hideIdleAnimation();
        this.coins = coins;
        if(this.coins > this.coinsTops.length)
            this.coins = this.coinsTops.length;
        this.displayCoins();
        if(this.coins === 2) {
            this.idle2AnimationToBeShown = true;
            this.showIdleAnimation();
        }
    }

    public addCoin() {
        if(this.coins < this.coinsTops.length) {
            this.coins++;
            this.hideIdleAnimation();
            if(this.coins === 2) this.idle2AnimationToBeShown = true;
            if(this.coins === 3) this.idleAnimationToBeShown = true;
            this.addCoinAnimation();
        } else {            
            const sprite = this.coinsBankedSprites[this.coinsBanked];
            sprite.visible = true;
            sprite.scale.set(0, 0);
            new TweenJS.Tween(sprite.scale)
            .to({x: 1, y: 1}, 150)                
            .start();
            this.coinsBanked++;         
        }            

    }

    public bankCoins() {    
        this.hideIdleAnimation();    
        if(this.coins > 0) {            
            this.coinsBanked = this.coins; 
            let index = 0;           
            while(this.coins > 0) {
                const sprites = this.coinsTopSprites[this.coins - 1];
                for(let sprite of sprites) {
                    sprite.visible = false;
                }
                const letter = Pot.LETTERS[this.coins - 1];
                const hideAnimation:AnimatedSprite = graphicsManager.createAnimation(`collect_coin_${letter}_hide`);
                hideAnimation.loop = false;
                hideAnimation.onComplete = () => { 
                    hideAnimation.visible = false;                    
                }
                const hideAnimationPosition = this.potsLayer.getPosition(`collect_pot${this.ID}_coin_${letter}_hide`);
                hideAnimation.setTransform(hideAnimationPosition.x, hideAnimationPosition.y);
                this.potsLayer.add(hideAnimation);
                hideAnimation.onComplete = () => { 
                    hideAnimation.visible = false;  // this.displayBankedCoins(); 
                    const sprite = this.coinsBankedSprites[index++];
                    sprite.visible = true;
                    sprite.scale.set(0, 0);
                    new TweenJS.Tween(sprite.scale)
                    .to({x: 1, y: 1}, 150)
                    .start();
                };                
                this.coins--;
                setTimeout(() => hideAnimation.play(), this.coins * 150);
            }
        }        
    }

    public restoreBankedCoins() {
        if(this.coinsBanked > 0) {
            this.coins = this.coinsBanked;
            this.coinsBanked = 0;
            if(this.coins > 0) {          
                for(let coin = 0; coin < this.coins; coin++) {                    
                    const letter = Pot.LETTERS[coin];
                    const bankedIndex = this.coins - coin - 1;
                    const banked = this.coinsBankedSprites[bankedIndex];
                    new TweenJS.Tween(banked.scale)
                    .to({x: 0, y: 0}, 150)
                    .delay(coin * 150)
                    .onComplete(() => {
                        banked.visible = false;
                        const unhideAnimation:AnimatedSprite = graphicsManager.createAnimation(`collect_coin_${letter}_unhide`);
                        unhideAnimation.loop = false;
                        unhideAnimation.onComplete = () => { 
                            unhideAnimation.visible = false; 
                            const sprites = this.coinsTopSprites[coin];
                            for(let sprite of sprites) {
                                sprite.visible = true;
                            }
                        }
                        const unhideAnimationPosition = this.potsLayer.getPosition(`collect_pot${this.ID}_coin_${letter}_unhide`);
                        unhideAnimation.setTransform(unhideAnimationPosition.x, unhideAnimationPosition.y);
                        this.potsLayer.add(unhideAnimation);
                        unhideAnimation.play();
                    }).start();                
                }  
                if(this.coins === 2) {
                    this.idle2AnimationToBeShown = true;
                    this.idleAnimationToBeShown = false;
                    this.showIdleAnimation();
                }                          
            }
        }
    }

    public triggerEmptyAnimation(amount: number, isfreespins: boolean = false, doublePots5x: boolean = false):Contract<void> {
        if(!this.triggered)
            return Contract.empty();        
        if(isfreespins && this.ID === 3) // todo constant, bonus id
            return this.triggerEmptyBonus();
        else if(isfreespins)
            return this.triggerEmptyOnFreeSpin(amount, doublePots5x);
        else
            return this.triggerEmpty3(amount, doublePots5x);
    }

    private triggerEmptyBonus():Contract<void> {
        this.triggered = false;
        const funcs: Array<() => Contract<void>> = [];
        this.hideIdleAnimation();
        while(this.coins > 0) {
            let letter = Pot.LETTERS[this.coins-1];
            let spriteString:string = `collect_pot_empty_coin_${letter}`;
            let positionString:string = `collect_pot${this.ID}_empty_coin_${letter}`;  
            let coin = this.coins - 1;                  
            
            funcs.push(() => {                
                return new Contract<void>(resolve=>{   
                    soundManager.customEvent("feature_pot_burst");                                                                  
                    this.shake().then(() => {                                                           
                        this.potSprite.scale.set(1, 1);
                        // hide coin sprite                               
                        const sprites = this.coinsTopSprites[coin];
                        for(let sprite of sprites) {
                            sprite.visible = false;
                        }
                        soundManager.customEvent("feature_pot_flip");
                        const emptyAnimation:AnimatedSprite = graphicsManager.createAnimation(spriteString);
                        const position = this.animationsLayer.getPosition(positionString);        
                        emptyAnimation.setTransform(position.x, position.y);
                        this.animationsLayer.add(emptyAnimation);
                        emptyAnimation.onComplete = () => resolve(null);
                        emptyAnimation.play();
                    });
                });
            });
            this.coins--;
        }
        return parallel(funcs);
    }

    private triggerEmptyOnFreeSpin(amount:number, doublePots5x:boolean = false):Contract<void> {
        this.triggered = false;
        const funcs: Array<() => Contract<void>> = [];
        this.hideIdleAnimation();
        const count = this.coins;
        while(this.coins > 0) {
            let letter = Pot.LETTERS[this.coins-1];
            let spriteString:string = `collect_pot_empty_coin_${letter}`;
            let positionString:string = `collect_pot${this.ID}_empty_coin_${letter}`;  
            let coin = this.coins - 1;                  
            
            funcs.push(() => {                
                return new Contract<void>(resolve=>{                                                                          
                    soundManager.customEvent("feature_pot_burst");   
                    this.shake().then(() => {                           
                        let done:boolean = false;                    
                        const lastCoin = coin === 0 && doublePots5x; 
                        const finished = () => { 
                            if(lastCoin !== done) {
                                resolve(null);                        
                            }                    
                            done = true; 
                        };                        
                        this.potSprite.scale.set(1, 1);
                        // hide coin sprite                               
                        const sprites = this.coinsTopSprites[coin];
                        for(let sprite of sprites) {
                            sprite.visible = false;
                        }
                        const glow = (this.ID === 1 || this.ID === 5) && !doublePots5x;
                        DSComponents.coinPotsTexts.showText(this.ID, amount, false, glow).then(finished); // is there a reason to wait for animation to finish or only the text
                        // show coin animation
                        soundManager.customEvent("feature_pot_flip"); 
                        const emptyAnimation:AnimatedSprite = graphicsManager.createAnimation(spriteString);
                        const position = this.animationsLayer.getPosition(positionString);        
                        emptyAnimation.setTransform(position.x, position.y);
                        this.animationsLayer.add(emptyAnimation);
                        emptyAnimation.onComplete = finished;
                        emptyAnimation.play();
                    });
                });
            });
            this.coins--;
        }
        return sequence(funcs);
    }

    private shake():Contract<void> {          
        return new Contract(resolve => {
            new TweenJS.Tween(this.potSprite)
            .to({rotation: this.shakeAngles}, 440)                           
            .start();
            new TweenJS.Tween(this.potSprite.scale)
            .to({x: 0.93, y: 0.93}, 370)
            .onComplete(() => {
                new TweenJS.Tween(this.potSprite.scale)
                .to({x: 1.1, y: 1.1}, 124)
                .onComplete(() => {
                    new TweenJS.Tween(this.potSprite.scale)
                    .to({x: 1, y: 1}, 62)
                    .onComplete(() => resolve(null))
                    .start();
                }).start();                
            }).start();            
        });
    }

    private triggerEmpty3(amount: number, doublePots5x: boolean = false):Contract<void> {
        this.triggered = false;
        return new Contract(resolve=>{   
            soundManager.customEvent("feature_pot_burst");                              
            this.hideIdleAnimation();            
            this.shake().then(() => {                     
                let done:boolean = false;
                const finished = () => {                     
                    if(doublePots5x !== done) {
                        resolve(null);                        
                    }                    
                    done = true; 
                };
                this.potSprite.scale.set(1, 1);
                this.coins = 0;
                this.displayCoins();
                const glow = (this.ID === 1 || this.ID === 5) && !doublePots5x;
                DSComponents.coinPotsTexts.showText(this.ID, amount, false, glow).then(finished);
                const emptyAnimation:AnimatedSprite = graphicsManager.createAnimation('collect_pot_empty3');
                const position = this.animationsLayer.getPosition(`collect_pot${this.ID}_empty_3`);        
                emptyAnimation.setTransform(position.x, position.y);
                this.animationsLayer.add(emptyAnimation);    
                emptyAnimation.onComplete = finished;            
                emptyAnimation.play();
            });                     
        });
    }

    public getNextCoinPosition(localLayer: Layers) : Rectangle {
        let letter;
        if(this.coins < Pot.LETTERS.length)
            letter = Pot.LETTERS[this.coins];
        else letter = "C";
        const dropPosition = Layers.Custom.get("CoinCollectPots").getPosition(`collect_pot${this.ID}_coin${letter}`);        
        return Layers.Custom.get("CoinCollectPots").localToLocal(localLayer, dropPosition);        
    }

    private displayCoins() {
        for(let i = 0; i < this.coinsTopSprites.length && i < this.coins; i++) {
            const sprites = this.coinsTopSprites[i];
            for(let sprite of sprites) {
                sprite.visible = true;
            }
        }        
        for(let i = this.coins; i < this.coinsTopSprites.length; i++) {
            const sprites = this.coinsTopSprites[i];
            for(let sprite of sprites) {
                sprite.visible = false;
            }
        }        
    }

    private displayBankedCoins() {
        for(let i = 0; i < this.coinsBankedSprites.length &&  i < this.coinsBanked; i++) {
            this.coinsBankedSprites[i].visible = true;
        }
        for(let i = this.coinsBanked; i < this.coinsBankedSprites.length; i++) {
            this.coinsBankedSprites[i].visible = false;
        }
    }

    private addCoinAnimation() {
        let highLightId;
        if(this.coins > 3)
            highLightId = this.coinHighlights.length - 1;
        else   
            highLightId = this.coins - 1;
        const highLight = this.potsLayer.getSprite(this.coinHighlights[highLightId]);
        this.displayCoins();
        if(highLight) {            
            highLight.visible = true;
            highLight.alpha = 1;
            new TweenJS.Tween(highLight)
            .to({ alpha: 0 }, 200)
            .easing(TweenJS.Easing.Linear.None)                    
            .onComplete(() => {                
                highLight.visible = false;
                if(this.coins === 3) {
                    this.showIdleAnimation();
                    soundManager.customEvent("feature_pot_full");
                } else if(this.coins === 2) {
                    this.showIdleAnimation();
                }
            })
            .start();
        }
    }

    private showIdleAnimation() {
        if(!this.idleAnimationToBeShown && !this.idle2AnimationToBeShown)
            return;

        if(this.idleAnimationToBeShown) {
            if(this.idle2AnimationToBeShown) this.idle2AnimationToBeShown = false;
            if(this.idle2Animation) {
                this.idle2Animation.stop();
                this.idle2Animation.visible = false;
            }
            // idle 3 coins
            if(!this.idleAnimation) {
                this.idleAnimation = graphicsManager.createAnimation('collect_pot_idle');
                const position = this.animationsLayer.getPosition(`collect_pot${this.ID}_idle`);        
                this.idleAnimation.setTransform(position.x, position.y);
                this.animationsLayer.add(this.idleAnimation);   
            } else 
                this.idleAnimation.visible = true;        
            this.idleAnimation.play();
        } else if(this.idle2AnimationToBeShown) {
            // 2 coins
            if(!this.idle2Animation) {
                this.idle2Animation = graphicsManager.createAnimation('collect_pot_idle_2_coins');
                const position = this.animationsLayer.getPosition(`collect_pot${this.ID}_idle_2_coins`);
                this.idle2Animation.setTransform(position.x, position.y);
                this.animationsLayer.add(this.idle2Animation);   
            } else 
                this.idle2Animation.visible = true;        
            this.idle2Animation.play();
        }
    }

    private hideIdleAnimation() {        
        this.idleAnimationToBeShown = false;
        this.idle2AnimationToBeShown = false;
        if(this.idleAnimation) {
            this.idleAnimation.stop();
            this.idleAnimation.visible = false;
        }
        if(this.idle2Animation) {
            this.idle2Animation.stop();
            this.idle2Animation.visible = false;
        }
    }
        
}